import React from "react";
import styled, { keyframes } from "styled-components";
import dayjs from "dayjs";

import IconButton from "../../../../components/button/IconButton";
import StatusIndicator, {
  type StatusType,
} from "../../../../components/status-indicator/StatusIndicator";

/**
 * 表の右下に常設する同期ステータスバー。
 *
 * 「いつサーバーと同期されたか」を可視化するのが目的で、
 * - 保存状態（保存中... / 保存済み / 保存に失敗しました）
 * - 再取得中インジケーター（同期中...）
 * - 最終同期時刻 + 手動同期ボタン
 * を 1 行にまとめる。データは RTK Query の `fulfilledTimeStamp` /
 * `isFetching` と `useTaskBoardSaveStatus`（プロジェクト単位）から供給される。
 */
interface TaskBoardSyncStatusProps {
  /** 最後に取得が完了した時刻（RTK Query の fulfilledTimeStamp, ms）。 */
  lastSyncedAt?: number;
  /** サーバーから再取得中か。 */
  isFetching: boolean;
  /** このボード群の保存状態（idle のときは非表示）。 */
  saveStatus?: StatusType;
  /** 手動同期（refetch）。 */
  onRefresh: () => void;
}

const spin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

const Bar = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  width: 100%;
  min-height: 20px;
  margin-top: 4px;
`;

const SyncText = styled.span`
  font-size: 11px;
  line-height: 1;
  color: var(--soremo-light-gray);
  white-space: nowrap;
  user-select: none;
`;

const SpinningIcon = styled.span.attrs({
  className: "material-symbols-rounded",
})`
  font-size: 14px;
  color: var(--soremo-light-gray);
  animation: ${spin} 1.2s linear infinite;
`;

const TaskBoardSyncStatus: React.FC<TaskBoardSyncStatusProps> = ({
  lastSyncedAt,
  isFetching,
  saveStatus = "idle",
  onRefresh,
}) => {
  return (
    <Bar aria-live="polite">
      <StatusIndicator status={saveStatus} inline withBottomAppBar={false} />
      {isFetching ? (
        <>
          <SpinningIcon aria-hidden="true">sync</SpinningIcon>
          <SyncText>同期中...</SyncText>
        </>
      ) : (
        <>
          {lastSyncedAt != null && (
            <SyncText>
              最終同期 {dayjs(lastSyncedAt).format("HH:mm")}
            </SyncText>
          )}
          <IconButton
            icon="sync"
            size={14}
            title="今すぐ同期"
            ariaLabel="今すぐ同期"
            onClick={onRefresh}
          />
        </>
      )}
    </Bar>
  );
};

export default TaskBoardSyncStatus;
